import { useState } from 'react';
import { format } from 'date-fns';
import pptxgen from 'pptxgenjs';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Clock,
  UserX,
  CalendarX,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  Layers,
  Users,
  Briefcase,
  ListTodo,
  Lightbulb,
} from 'lucide-react';
import { cn } from '../lib/utils';

const SEVERITY_STYLES = {
  critical: {
    icon: AlertCircle,
    badge: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
    dot: 'bg-red-500',
    iconColor: 'text-red-600 dark:text-red-400',
    rowBg: 'bg-red-50/50 dark:bg-red-950/20',
  },
  warning: {
    icon: AlertTriangle,
    badge: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
    dot: 'bg-amber-500',
    iconColor: 'text-amber-600 dark:text-amber-400',
    rowBg: 'bg-amber-50/50 dark:bg-amber-950/20',
  },
  info: {
    icon: Info,
    badge: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900',
    dot: 'bg-blue-500',
    iconColor: 'text-blue-600 dark:text-blue-400',
    rowBg: 'bg-blue-50/50 dark:bg-blue-950/20',
  },
};

const PRIORITY_STYLES = {
  high: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
  medium: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  low: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
};

const ISSUE_KIND_META = {
  overdue: { icon: Clock, label: 'Scaduto' },
  no_resources: { icon: UserX, label: 'Senza risorse' },
  no_dates: { icon: CalendarX, label: 'Date mancanti' },
  behind_schedule: { icon: TrendingDown, label: 'In ritardo' },
  other: { icon: Info, label: 'Altro' },
};

function getSeverity(value) {
  return SEVERITY_STYLES[value] || SEVERITY_STYLES.info;
}

function SeverityBadge({ severity }) {
  const s = getSeverity(severity);
  const Icon = s.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border', s.badge)}>
      <Icon className="w-3 h-3" />
      {severity}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const cls = PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border uppercase tracking-wide', cls)}>
      {priority}
    </span>
  );
}

function ReferenceChip({ reference, onTaskClick, onProjectClick, onResourceClick }) {
  if (!reference || !reference.id) return null;
  const label = reference.label || reference.id.slice(0, 8);
  const kindLabel = reference.kind === 'task' ? 'Task' : reference.kind === 'project' ? 'Progetto' : 'Risorsa';
  const handleClick = function() {
    if (reference.kind === 'task' && onTaskClick) onTaskClick(reference.id);
    else if (reference.kind === 'project' && onProjectClick) onProjectClick(reference.id);
    else if (reference.kind === 'resource' && onResourceClick) onResourceClick(reference.id);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-muted hover:bg-primary/10 hover:text-primary border border-border transition-colors"
    >
      <span className="text-muted-foreground">{kindLabel}:</span>
      <span className="font-medium">{label}</span>
    </button>
  );
}

function MainInsightItem({ item, onTaskClick, onProjectClick, onResourceClick }) {
  const s = getSeverity(item.severity);
  const Icon = s.icon;
  const refs = Array.isArray(item.references) ? item.references : [];
  const refNodes = [];
  for (let i = 0; i < refs.length; i++) {
    refNodes.push(
      <ReferenceChip
        key={i}
        reference={refs[i]}
        onTaskClick={onTaskClick}
        onProjectClick={onProjectClick}
        onResourceClick={onResourceClick}
      />
    );
  }
  return (
    <div className={cn('flex gap-3 p-3 rounded-md border border-border', s.rowBg)}>
      <Icon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', s.iconColor)} />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="text-sm font-medium">{item.title}</div>
        {item.description && (
          <div className="text-sm text-muted-foreground whitespace-pre-wrap">{item.description}</div>
        )}
        {refNodes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">{refNodes}</div>
        )}
      </div>
    </div>
  );
}

function IssueCounters({ issues }) {
  const counters = { critical: 0, warning: 0, info: 0 };
  for (const iss of issues || []) {
    if (counters[iss.severity] !== undefined) counters[iss.severity] += 1;
  }
  const items = [
    { sev: 'critical', count: counters.critical },
    { sev: 'warning', count: counters.warning },
    { sev: 'info', count: counters.info },
  ].filter(function(x) { return x.count > 0; });
  if (items.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5">
      {items.map(function(x) {
        const s = getSeverity(x.sev);
        return (
          <span key={x.sev} className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border', s.badge)}>
            <span className={cn('w-1.5 h-1.5 rounded-full', s.dot)} />
            {x.count}
          </span>
        );
      })}
    </div>
  );
}

function ProjectInsightCard({ project, onTaskClick, onProjectClick, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const issues = Array.isArray(project.issues) ? project.issues : [];
  const issueCount = issues.length;
  const issueNodes = [];
  for (let idx = 0; idx < issues.length; idx++) {
    const iss = issues[idx];
    const meta = ISSUE_KIND_META[iss.kind] || ISSUE_KIND_META.other;
    const Icon = meta.icon;
    const s = getSeverity(iss.severity);
    issueNodes.push(
      <div key={idx} className={cn('flex items-start gap-2 p-2 rounded border border-border', s.rowBg)}>
        <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', s.iconColor)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{meta.label}</span>
            <SeverityBadge severity={iss.severity} />
          </div>
          <div className="text-sm">{iss.description}</div>
          {iss.taskId && onTaskClick && (
            <button
              type="button"
              onClick={function() { onTaskClick(iss.taskId); }}
              className="text-xs text-primary hover:underline mt-0.5"
            >
              Apri task
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        type="button"
        onClick={function() { setOpen(!open); }}
        className="w-full flex items-center gap-2 px-3 py-2 bg-muted/40 hover:bg-muted text-left transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <span className="text-sm font-medium flex-1 truncate">
          {project.projectName || project.projectId}
        </span>
        <span className="text-xs text-muted-foreground">{issueCount} {issueCount === 1 ? 'issue' : 'issues'}</span>
        <IssueCounters issues={issues} />
        {onProjectClick && (
          <button
            type="button"
            onClick={function(e) { e.stopPropagation(); onProjectClick(project.projectId); }}
            className="ml-2 text-xs text-primary hover:underline"
          >
            apri
          </button>
        )}
      </button>
      {open && (
        <div className="p-3 space-y-2 bg-card">
          {project.summary && (
            <p className="text-sm text-muted-foreground">{project.summary}</p>
          )}
          {issueCount === 0 && (
            <p className="text-xs text-muted-foreground italic">Nessuna issue rilevata.</p>
          )}
          {issueNodes}
        </div>
      )}
    </div>
  );
}

function CriticalWorkloadsTable({ workloads, onResourceClick }) {
  if (!Array.isArray(workloads) || workloads.length === 0) {
    return <p className="text-sm text-muted-foreground italic">Nessun sovraccarico rilevato nella finestra analizzata.</p>;
  }
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Risorsa</th>
            <th className="text-left px-3 py-2 font-medium">Settimana</th>
            <th className="text-right px-3 py-2 font-medium">Ore</th>
            <th className="text-right px-3 py-2 font-medium">Overload</th>
            <th className="text-left px-3 py-2 font-medium">Severity</th>
          </tr>
        </thead>
        <tbody>
          {workloads.map(function(w, idx) {
            const s = getSeverity(w.severity);
            return (
              <tr
                key={idx}
                className={cn('border-t border-border', onResourceClick && 'cursor-pointer hover:bg-muted/40', s.rowBg)}
                onClick={onResourceClick ? function() { onResourceClick(w.resourceId); } : undefined}
              >
                <td className="px-3 py-2 font-medium">{w.resourceName || w.resourceId}</td>
                <td className="px-3 py-2 text-muted-foreground">{w.week}</td>
                <td className="px-3 py-2 text-right tabular-nums">{w.hours.toFixed(1)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-destructive">+{w.overloadHours.toFixed(1)}</td>
                <td className="px-3 py-2"><SeverityBadge severity={w.severity} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RecommendedActionItem({ action, onTaskClick, onProjectClick, onResourceClick }) {
  const refs = Array.isArray(action.references) ? action.references : [];
  const refNodes = [];
  for (let i = 0; i < refs.length; i++) {
    refNodes.push(
      <ReferenceChip
        key={i}
        reference={refs[i]}
        onTaskClick={onTaskClick}
        onProjectClick={onProjectClick}
        onResourceClick={onResourceClick}
      />
    );
  }
  return (
    <div className="flex gap-3 p-3 rounded-md border border-border">
      <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-primary" />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{action.title}</span>
          <PriorityBadge priority={action.priority} />
        </div>
        {action.description && (
          <p className="text-sm text-muted-foreground">{action.description}</p>
        )}
        {refNodes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">{refNodes}</div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, count }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-primary" />
      <h3 className="text-sm font-semibold">{title}</h3>
      {typeof count === 'number' && count > 0 && (
        <span className="text-xs text-muted-foreground">({count})</span>
      )}
    </div>
  );
}

// ============================================================
// PPTX export
// ============================================================

const PPT_BRAND_BLUE = '0072BC';
const PPT_DARK = '1F2937';
const PPT_MUTED = '6B7280';
const PPT_LIGHT_BG = 'F9FAFB';
const PPT_WHITE = 'FFFFFF';
const PPT_GREY_BORDER = 'E5E7EB';
const PPT_RED = 'DC2626';
const PPT_AMBER = 'D97706';
const PPT_BLUE = '2563EB';
const PPT_SLATE = '64748B';

const SLIDE_W = 13.333;
const SLIDE_H = 7.5;

const PPT_SEVERITY = {
  critical: { fill: 'FEE2E2', text: PPT_RED, label: 'CRITICO' },
  warning:  { fill: 'FEF3C7', text: PPT_AMBER, label: 'WARNING' },
  info:     { fill: 'DBEAFE', text: PPT_BLUE, label: 'INFO' }
};

const PPT_PRIORITY = {
  high:   { fill: 'FEE2E2', text: PPT_RED, label: 'ALTA' },
  medium: { fill: 'FEF3C7', text: PPT_AMBER, label: 'MEDIA' },
  low:    { fill: 'F1F5F9', text: PPT_SLATE, label: 'BASSA' }
};

const PPT_ISSUE_KIND_LABELS = {
  overdue: 'Scaduto',
  no_resources: 'Senza risorse',
  no_dates: 'Date mancanti',
  behind_schedule: 'In ritardo',
  other: 'Altro'
};

const severityMeta = function(sev) {
  return PPT_SEVERITY[sev] || PPT_SEVERITY.info;
};

const priorityMeta = function(pri) {
  return PPT_PRIORITY[pri] || PPT_PRIORITY.medium;
};

const chunkArray = function(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const defineAiMasters = function(pres, insight) {
  const createdAt = insight && insight.createdAt ? new Date(insight.createdAt) : new Date();
  const footerText = 'Analisi AI · ' + format(createdAt, 'dd/MM/yyyy HH:mm');
  pres.defineSlideMaster({
    title: 'BASE',
    background: { color: PPT_WHITE },
    objects: [
      { rect: { x: 0, y: 0, w: SLIDE_W, h: 0.08, fill: { color: PPT_BRAND_BLUE } } },
      { line: { x: 0.5, y: SLIDE_H - 0.45, w: SLIDE_W - 1, h: 0, line: { color: PPT_GREY_BORDER, width: 0.75 } } },
      { text: { text: footerText, options: { x: 0.5, y: SLIDE_H - 0.4, w: SLIDE_W - 1, h: 0.3, fontSize: 9, color: PPT_MUTED, fontFace: 'Calibri', align: 'left', valign: 'middle' } } }
    ],
    slideNumber: { x: SLIDE_W - 1.0, y: SLIDE_H - 0.4, w: 0.5, h: 0.3, fontSize: 9, color: PPT_MUTED, fontFace: 'Calibri', align: 'right', valign: 'middle' }
  });
  pres.defineSlideMaster({
    title: 'COVER',
    background: { color: PPT_WHITE },
    objects: [
      { rect: { x: 0, y: 0, w: 0.4, h: SLIDE_H, fill: { color: PPT_BRAND_BLUE } } }
    ]
  });
};

const addAiSlideTitle = function(slide, text) {
  slide.addText(text, {
    x: 0.5, y: 0.25, w: SLIDE_W - 1, h: 0.5,
    fontSize: 24, bold: true, color: PPT_DARK, fontFace: 'Calibri'
  });
};

const addAiCoverSlide = function(pres, insight) {
  const slide = pres.addSlide({ masterName: 'COVER' });
  const createdAt = insight && insight.createdAt ? new Date(insight.createdAt) : new Date();
  slide.addText('Analisi AI', {
    x: 1, y: 2.5, w: 11, h: 1,
    fontSize: 48, bold: true, color: PPT_DARK, fontFace: 'Calibri'
  });
  slide.addText('Insight sui progetti', {
    x: 1, y: 3.6, w: 11, h: 0.7,
    fontSize: 28, color: PPT_BRAND_BLUE, fontFace: 'Calibri'
  });
  const meta = [];
  if (insight && insight.model) meta.push('Modello: ' + insight.model);
  if (insight && insight.provider) meta.push('Provider: ' + insight.provider);
  if (meta.length > 0) {
    slide.addText(meta.join('  ·  '), {
      x: 1, y: 4.5, w: 11, h: 0.4,
      fontSize: 14, color: PPT_MUTED, fontFace: 'Calibri'
    });
  }
  slide.addText('Generato il ' + format(createdAt, 'dd/MM/yyyy HH:mm'), {
    x: 1, y: 6.5, w: 11, h: 0.4,
    fontSize: 12, color: PPT_MUTED, fontFace: 'Calibri'
  });
};

const addEmptySlide = function(pres, title, message) {
  const slide = pres.addSlide({ masterName: 'BASE' });
  addAiSlideTitle(slide, title);
  slide.addText(message, {
    x: 0.5, y: 3, w: SLIDE_W - 1, h: 1,
    fontSize: 16, color: PPT_MUTED, align: 'center', italic: true, fontFace: 'Calibri'
  });
};

const addMainInsightsSlides = function(pres, insight) {
  const items = (insight.sections && insight.sections.mainInsights) || [];
  if (items.length === 0) {
    addEmptySlide(pres, 'Insight principali', 'Nessun insight generale rilevante.');
    return;
  }
  const pages = chunkArray(items, 7);
  for (let p = 0; p < pages.length; p++) {
    const slide = pres.addSlide({ masterName: 'BASE' });
    const suffix = pages.length > 1 ? ' (' + (p + 1) + '/' + pages.length + ')' : '';
    addAiSlideTitle(slide, 'Insight principali' + suffix);
    const rows = [[
      { text: 'Severity',    options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, align: 'left' } },
      { text: 'Titolo',      options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, align: 'left' } },
      { text: 'Descrizione', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, align: 'left' } }
    ]];
    for (const it of pages[p]) {
      const sm = severityMeta(it.severity);
      rows.push([
        { text: sm.label, options: { bold: true, color: sm.text, fill: { color: sm.fill }, align: 'center' } },
        { text: it.title || '', options: { color: PPT_DARK, bold: true } },
        { text: it.description || '', options: { color: PPT_DARK } }
      ]);
    }
    slide.addTable(rows, {
      x: 0.5, y: 1.1, w: SLIDE_W - 1,
      colW: [1.4, 3.5, SLIDE_W - 1 - 1.4 - 3.5],
      fontSize: 10, fontFace: 'Calibri',
      border: { type: 'solid', color: PPT_GREY_BORDER, pt: 0.5 },
      valign: 'middle',
      autoPage: false
    });
  }
};

const addProjectBreakdownSlides = function(pres, insight) {
  const projects = (insight.sections && insight.sections.projectInsights) || [];
  const withIssues = projects.filter(function(p) {
    return Array.isArray(p.issues) && p.issues.length > 0;
  });
  if (withIssues.length === 0) {
    addEmptySlide(pres, 'Dettaglio progetti', 'Nessun progetto con issue rilevate.');
    return;
  }
  for (const project of withIssues) {
    const issues = project.issues || [];
    const pages = chunkArray(issues, 8);
    for (let p = 0; p < pages.length; p++) {
      const slide = pres.addSlide({ masterName: 'BASE' });
      const titleSuffix = pages.length > 1 ? ' (' + (p + 1) + '/' + pages.length + ')' : '';
      addAiSlideTitle(slide, (project.projectName || project.projectId || 'Progetto') + titleSuffix);

      let tableY = 1.1;
      if (p === 0 && project.summary) {
        slide.addShape('rect', {
          x: 0.5, y: 1.0, w: SLIDE_W - 1, h: 0.8,
          fill: { color: PPT_LIGHT_BG },
          line: { color: PPT_GREY_BORDER, width: 0.5 }
        });
        slide.addText(project.summary, {
          x: 0.65, y: 1.05, w: SLIDE_W - 1.3, h: 0.7,
          fontSize: 11, color: PPT_DARK, fontFace: 'Calibri', valign: 'middle', italic: true
        });
        tableY = 2.0;
      }

      const rows = [[
        { text: 'Tipo',        options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE } } },
        { text: 'Severity',    options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE } } },
        { text: 'Descrizione', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE } } }
      ]];
      for (const iss of pages[p]) {
        const sm = severityMeta(iss.severity);
        const kindLabel = PPT_ISSUE_KIND_LABELS[iss.kind] || PPT_ISSUE_KIND_LABELS.other;
        rows.push([
          { text: kindLabel, options: { color: PPT_DARK, bold: true } },
          { text: sm.label,  options: { bold: true, color: sm.text, fill: { color: sm.fill }, align: 'center' } },
          { text: iss.description || '', options: { color: PPT_DARK } }
        ]);
      }
      slide.addTable(rows, {
        x: 0.5, y: tableY, w: SLIDE_W - 1,
        colW: [2.2, 1.4, SLIDE_W - 1 - 2.2 - 1.4],
        fontSize: 10, fontFace: 'Calibri',
        border: { type: 'solid', color: PPT_GREY_BORDER, pt: 0.5 },
        valign: 'middle',
        autoPage: false
      });
    }
  }
};

const addCriticalWorkloadsSlides = function(pres, insight) {
  const workloads = (insight.sections && insight.sections.criticalWorkloads) || [];
  const wnd = insight.workloadsWindow;
  if (workloads.length === 0) {
    addEmptySlide(pres, 'Carichi critici', 'Nessun sovraccarico rilevato nella finestra analizzata.');
    return;
  }
  const pages = chunkArray(workloads, 10);
  for (let p = 0; p < pages.length; p++) {
    const slide = pres.addSlide({ masterName: 'BASE' });
    const suffix = pages.length > 1 ? ' (' + (p + 1) + '/' + pages.length + ')' : '';
    addAiSlideTitle(slide, 'Carichi critici' + suffix);
    let tableY = 1.1;
    if (p === 0 && wnd && wnd.fromWeek) {
      slide.addText(
        'Finestra: ' + wnd.fromWeek + ' → ' + wnd.toWeek + ' (' + wnd.horizonWeeks + ' settimane)',
        { x: 0.5, y: 0.85, w: SLIDE_W - 1, h: 0.3, fontSize: 11, color: PPT_MUTED, italic: true, fontFace: 'Calibri' }
      );
      tableY = 1.25;
    }
    const rows = [[
      { text: 'Risorsa',   options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE } } },
      { text: 'Settimana', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE } } },
      { text: 'Ore',       options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, align: 'right' } },
      { text: 'Overload',  options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, align: 'right' } },
      { text: 'Severity',  options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE } } }
    ]];
    for (const w of pages[p]) {
      const sm = severityMeta(w.severity);
      rows.push([
        { text: w.resourceName || w.resourceId || '', options: { color: PPT_DARK, bold: true } },
        { text: w.week || '', options: { color: PPT_MUTED } },
        { text: (Number.isFinite(w.hours) ? w.hours.toFixed(1) : 'n/d'), options: { color: PPT_DARK, align: 'right' } },
        { text: (Number.isFinite(w.overloadHours) ? '+' + w.overloadHours.toFixed(1) : 'n/d'), options: { color: PPT_RED, bold: true, align: 'right' } },
        { text: sm.label, options: { bold: true, color: sm.text, fill: { color: sm.fill }, align: 'center' } }
      ]);
    }
    slide.addTable(rows, {
      x: 0.5, y: tableY, w: SLIDE_W - 1,
      colW: [3.5, 2.0, 1.6, 1.8, SLIDE_W - 1 - 3.5 - 2.0 - 1.6 - 1.8],
      fontSize: 10, fontFace: 'Calibri',
      border: { type: 'solid', color: PPT_GREY_BORDER, pt: 0.5 },
      valign: 'middle',
      autoPage: false
    });
  }
};

const addRecommendedActionsSlides = function(pres, insight) {
  const actions = (insight.sections && insight.sections.recommendedActions) || [];
  if (actions.length === 0) {
    addEmptySlide(pres, 'Azioni consigliate', 'Nessuna azione specifica suggerita.');
    return;
  }
  const pages = chunkArray(actions, 5);
  for (let p = 0; p < pages.length; p++) {
    const slide = pres.addSlide({ masterName: 'BASE' });
    const suffix = pages.length > 1 ? ' (' + (p + 1) + '/' + pages.length + ')' : '';
    addAiSlideTitle(slide, 'Azioni consigliate' + suffix);
    const cardH = 1.05;
    const gap = 0.15;
    const startY = 1.1;
    for (let i = 0; i < pages[p].length; i++) {
      const a = pages[p][i];
      const pm = priorityMeta(a.priority);
      const y = startY + i * (cardH + gap);
      slide.addShape('rect', {
        x: 0.5, y: y, w: SLIDE_W - 1, h: cardH,
        fill: { color: PPT_WHITE },
        line: { color: PPT_GREY_BORDER, width: 0.5 }
      });
      slide.addShape('rect', {
        x: 0.5, y: y, w: 0.12, h: cardH,
        fill: { color: pm.text }, line: { type: 'none' }
      });
      slide.addText(pm.label, {
        x: 0.78, y: y + 0.15, w: 1.0, h: 0.32,
        fontSize: 9, bold: true, color: pm.text, fill: { color: pm.fill },
        align: 'center', valign: 'middle', fontFace: 'Calibri'
      });
      slide.addText(a.title || '', {
        x: 1.95, y: y + 0.08, w: SLIDE_W - 2.6, h: 0.4,
        fontSize: 14, bold: true, color: PPT_DARK, fontFace: 'Calibri', valign: 'middle'
      });
      if (a.description) {
        slide.addText(a.description, {
          x: 1.95, y: y + 0.5, w: SLIDE_W - 2.6, h: cardH - 0.55,
          fontSize: 10, color: PPT_MUTED, fontFace: 'Calibri', valign: 'top'
        });
      }
    }
  }
};

export const exportAiInsightToPptx = function(insight) {
  if (!insight || !insight.sections) return;
  const pres = new pptxgen();
  pres.layout = 'LAYOUT_WIDE';
  pres.title = 'Analisi AI';
  pres.company = 'Analisi AI';

  defineAiMasters(pres, insight);
  addAiCoverSlide(pres, insight);
  addMainInsightsSlides(pres, insight);
  addProjectBreakdownSlides(pres, insight);
  addCriticalWorkloadsSlides(pres, insight);
  addRecommendedActionsSlides(pres, insight);

  const filename = 'analisi-ai-' + format(new Date(), 'yyyyMMdd-HHmm') + '.pptx';
  pres.writeFile({ fileName: filename });
};

export const AiInsightView = ({ insight, onTaskClick, onProjectClick, onResourceClick }) => {
  const sections = insight && insight.sections;
  const window = insight && insight.workloadsWindow;

  // Fallback retro-compatibilità: solo testo libero senza sections
  if (!sections) {
    return (
      <textarea
        readOnly
        value={(insight && insight.text) || ''}
        placeholder={'Clicca su "Analisi AI" per generare un insight sui tuoi progetti...'}
        className="w-full h-full min-h-[300px] resize-none rounded-md border border-input bg-muted/30 p-4 text-sm leading-relaxed focus:outline-none"
      />
    );
  }

  const main = sections.mainInsights || [];
  const perProject = sections.projectInsights || [];
  const workloads = sections.criticalWorkloads || [];
  const actions = sections.recommendedActions || [];

  return (
    <div className="space-y-6 pb-4">
      {/* 1) Insight principali */}
      <section>
        <SectionHeader icon={Layers} title="Insight principali" count={main.length} />
        {main.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nessun insight generale rilevante.</p>
        ) : (
          <div className="space-y-2">
            {main.map(function(it, idx) {
              return (
                <MainInsightItem
                  key={idx}
                  item={it}
                  onTaskClick={onTaskClick}
                  onProjectClick={onProjectClick}
                  onResourceClick={onResourceClick}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* 2) Insight per progetto */}
      <section>
        <SectionHeader icon={Briefcase} title="Insight per progetto" count={perProject.length} />
        {perProject.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nessun progetto analizzato.</p>
        ) : (
          <div className="space-y-2">
            {perProject.map(function(p, idx) {
              return (
                <ProjectInsightCard
                  key={p.projectId || idx}
                  project={p}
                  onTaskClick={onTaskClick}
                  onProjectClick={onProjectClick}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* 3) Carichi critici */}
      <section>
        <SectionHeader icon={Users} title="Carichi critici" count={workloads.length} />
        {window && window.fromWeek && (
          <p className="text-xs text-muted-foreground mb-2">
            Finestra: {window.fromWeek} → {window.toWeek} ({window.horizonWeeks} settimane dalla corrente)
          </p>
        )}
        <CriticalWorkloadsTable workloads={workloads} onResourceClick={onResourceClick} />
      </section>

      {/* 4) Azioni consigliate */}
      <section>
        <SectionHeader icon={ListTodo} title="Azioni consigliate" count={actions.length} />
        {actions.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nessuna azione specifica suggerita.</p>
        ) : (
          <div className="space-y-2">
            {actions.map(function(a, idx) {
              return (
                <RecommendedActionItem
                  key={idx}
                  action={a}
                  onTaskClick={onTaskClick}
                  onProjectClick={onProjectClick}
                  onResourceClick={onResourceClick}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AiInsightView;
